import React from 'react';
import { styled } from '@withjoy/joykit';
import { useFormControl } from '@withjoy/joykit/components/FormControl/FormControl.provider';
import { IntlTelInputWrapper } from './IntlTelInputWrapper';

type PhoneFieldProps = Readonly<{
  id?: string;
  /** A number to start from, in any format intl-tel-input reads. */
  initialValue?: string | null;
  /** Receives the number as E.164 when it can be read, as typed otherwise. */
  onChangeNumber: (number: string) => void;
  onChangeValidity?: (isValid: boolean) => void;
  onBlur?: () => void;
  /** The country the picker starts on, as an ISO 3166-1 alpha-2 code ("us"). */
  initialCountry: string;
  /** Change it to reset the field (intl-tel-input manipulates the DOM, so a React key can't). */
  resetKey?: React.Key | null;
  /**
   * Take focus as soon as the field is ready. For a field revealed by a press that removed the
   * focused control, so focus lands on the field rather than the document.
   */
  enableFocusOnMount?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  'aria-describedby'?: string;
  /** Set by FormControl when it shows an error; the input is described by it. */
  'aria-errormessage'?: string;
}>;

/**
 * A phone number field with a country picker, drawn on the shared field contract: a 1px
 * `inputBorder` hairline, a 2px `mono6` ring on hover, a 2px `inputBorderFocused` ring while the
 * number or the picker has focus, a 2px `inputBorderError` ring when invalid, and no ring when
 * disabled (precedence disabled > invalid > focus > hover), at `radii.field`.
 *
 * intl-tel-input renders its own `<input>`, which no JoyKit field can wrap, so the contract is drawn
 * on this wrapper and the input inside is borderless (the composite pattern in
 * doc/field-focus-consistency/EXCEPTIONS.md). Invalid and disabled come from the props or from the
 * surrounding FormControl.
 */
export const PhoneField: React.FC<PhoneFieldProps> = ({
  id,
  initialValue,
  onChangeNumber,
  onChangeValidity,
  onBlur,
  initialCountry,
  resetKey,
  enableFocusOnMount,
  isInvalid,
  isDisabled,
  'aria-describedby': ariaDescribedBy,
  'aria-errormessage': ariaErrorMessage,
}) => {
  const formControl = useFormControl({ isInvalid, isDisabled, isRequired: undefined });
  return (
    <StyledPhoneField
      data-invalid={formControl.isInvalid || undefined}
      data-disabled={formControl.isDisabled || undefined}
    >
      <IntlTelInputWrapper
        resetKey={resetKey}
        enableFocusOnMount={enableFocusOnMount}
        initialValue={initialValue}
        onChangeNumber={onChangeNumber}
        onChangeValidity={onChangeValidity}
        initOptions={{
          initialCountry,
          countryOrder: Array.from(new Set([initialCountry, 'us', 'ca', 'gb', 'au'])),
          formatOnDisplay: true,
          strictMode: true,
          useFullscreenPopup: false,
          // No sample number: a complete one read as a field already filled in (design decision).
          autoPlaceholder: 'off',
        }}
        inputProps={{
          id,
          autoComplete: 'tel',
          disabled: formControl.isDisabled,
          'aria-invalid': formControl.isInvalid || undefined,
          'aria-describedby': ariaDescribedBy ?? ariaErrorMessage,
          'aria-errormessage': ariaErrorMessage,
          'aria-required': formControl.isRequired || undefined,
          onBlur,
        }}
      />
    </StyledPhoneField>
  );
};

/** Exported so an event-themed surface can paint the field's fill and hairline from its palette. */
export const StyledPhoneField = styled.div`
  position: relative;
  width: 100%;
  border: 1px solid ${props => props.theme.colors.inputBorder};
  border-radius: ${props => props.theme.radii.field};
  background-color: ${props => props.theme.colors.inputFill};
  color: ${props => props.theme.colors.inputText};

  /* The ring. Its colour follows the state; rest is the same ring at transparent. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    box-shadow: 0 0 0 2px transparent;
    transition: box-shadow 150ms ease;
  }
  &:hover::after {
    box-shadow: 0 0 0 2px ${props => props.theme.colors.mono6};
  }
  &:focus-within::after {
    box-shadow: 0 0 0 2px ${props => props.theme.colors.inputBorderFocused};
  }
  &[data-invalid]::after {
    box-shadow: 0 0 0 2px ${props => props.theme.colors.inputBorderError};
  }
  &[data-disabled] {
    color: ${props => props.theme.colors.inputTextDisabled};
    background-color: ${props => props.theme.colors.inputFillDisabled};
  }
  &[data-disabled]::after {
    box-shadow: none;
  }

  .iti {
    display: block;
    width: 100%;
  }
  .iti__tel-input {
    width: 100%;
    height: ${props => props.theme.space[9]};
    border: 0;
    outline: 0;
    background: transparent;
    color: inherit;
    font-size: ${props => props.theme.fontSizes[500]};
    padding-right: ${props => props.theme.space[5]};
  }
  .iti__tel-input::placeholder {
    color: ${props => props.theme.colors.inputPlaceholder};
  }
  .iti__country-list {
    white-space: normal;
  }
`;
